import { API_URLS } from '../../../routes/urls';
import { PaginatedListParams, PaginatedListResponse, StrOrNum } from '../../../types/util';
import { apiSlice } from '../../apiSlice';
import {
  transformPaginatedResponseFilters,
  processFiltersFromResponse,
} from '../../../components/paginated-list/helper';
import { ScholarshipListItem } from '../../../types/scholarship';
import {
  EligibleScholarshipIdsResponse,
  GetSingleScholarshipResponse,
  RawScholarshipListPageResponse,
  TransformedScholarshipListPageResponse,
} from './types';
import { generatePath } from 'react-router-dom';
import { QueryErrorReturnValue } from '../../types';
import { addSnackbar } from '../snackbar/snackbarSlice';
import { convertObjectToFormData } from '../../../utils/form';

const scholarshipApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listScholarships: builder.query<PaginatedListResponse<ScholarshipListItem>, PaginatedListParams | void>({
      query: (params) => ({
        url: API_URLS.scholarships,
        params: {
          ...params,
          limit: 12,
        },
      }),
      transformResponse: (response: PaginatedListResponse<ScholarshipListItem>) =>
        transformPaginatedResponseFilters(response),
    }),
    scholarshipListPage: builder.query<TransformedScholarshipListPageResponse, void>({
      query: () => ({
        url: API_URLS.scholarshipPage,
      }),
      transformResponse: (response: RawScholarshipListPageResponse) => {
        const newFilter = processFiltersFromResponse(response.filter) || [];
        return {
          region: response.region.map((region) => ({
            label: region.name,
            id: region.id,
            value: region.id,
          })),
          filter: newFilter,
        };
      },
    }),
    singleScholarship: builder.query<GetSingleScholarshipResponse, string>({
      query: (scholarshipSlug) => ({
        url: generatePath(API_URLS.singleScholarship, { scholarshipSlug }),
      }),
    }),
    featuredScholarships: builder.query<ScholarshipListItem[], void>({
      query: () => ({
        url: API_URLS.scholarships,
        params: {
          homepage: 1,
          featured: 1,
        },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (error) {
          const err = error as QueryErrorReturnValue;
          dispatch(
            addSnackbar({
              message: err.error.message,
              severity: 'error',
            })
          );
        }
      },
    }),

    eligibleScholarshipIds: builder.query<EligibleScholarshipIdsResponse, PaginatedListParams>({
      query: (params) => ({
        url: API_URLS.eligibleScholarshipIds,
        params,
      }),
      transformResponse: (response: EligibleScholarshipIdsResponse) => transformPaginatedResponseFilters(response),
      providesTags: ['EligibleModule'],
    }),

    scholarshipByIds: builder.query<ScholarshipListItem[], StrOrNum[]>({
      query: (ids) => ({
        url: API_URLS.scholarshipByIds,
        method: 'POST',
        data: convertObjectToFormData({ ids }),
      }),
    }),
  }),
});

export const {
  useListScholarshipsQuery,
  useScholarshipListPageQuery,
  useSingleScholarshipQuery,
  useFeaturedScholarshipsQuery,
  useEligibleScholarshipIdsQuery,
  useLazyScholarshipByIdsQuery,
} = scholarshipApi;
